import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { FAMILIARITY_LEVELS, type Familiarity } from "@itspip/shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const LABELS: Record<Familiarity, { title: string; hint: string }> = {
  novice: {
    title: "New to OKRs",
    hint: "The coach will teach the concept first, then evaluate.",
  },
  developing: {
    title: "Some experience",
    hint: "The coach will offer structural hints and push you on gaps.",
  },
  fluent: {
    title: "Fluent",
    hint: "The coach will push ambition and crispness — no scaffolds.",
  },
};

interface FamiliarityControlProps {
  value: Familiarity;
  onChange: (next: Familiarity) => void;
  /** Shown after the user confirms a change. */
  pending?: boolean;
}

export function FamiliarityControl({
  value,
  onChange,
  pending,
}: FamiliarityControlProps) {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label>What is your experience level creating OKRs?</Label>
        <Popover open={helpOpen} onOpenChange={setHelpOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-[hsl(var(--muted-foreground))]"
            >
              <HelpCircle className="mr-1 h-3.5 w-3.5" />
              Is this right?
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 text-sm">
            <p className="mb-2 font-semibold">How the coach uses this</p>
            <ul className="space-y-1 text-[hsl(var(--muted-foreground))]">
              <li>
                <span className="font-medium text-foreground">Novice</span> — a
                brief teaching turn before evaluation.
              </li>
              <li>
                <span className="font-medium text-foreground">Developing</span> —
                coaching with structural scaffolds.
              </li>
              <li>
                <span className="font-medium text-foreground">Fluent</span> —
                hard coaching, no hand-holding.
              </li>
            </ul>
            <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
              The coach may silently drop to a simpler mode if a draft reveals
              a big gap. Your choice is saved and applies to all future coaching sessions.
            </p>
          </PopoverContent>
        </Popover>
        {pending && (
          <span className="text-xs text-[hsl(var(--muted-foreground))]">
            Saving…
          </span>
        )}
      </div>
      <div
        role="radiogroup"
        aria-label="Experience level creating OKRs"
        className="grid grid-cols-3 gap-2"
      >
        {FAMILIARITY_LEVELS.map((level) => {
          const isActive = value === level;
          return (
            <button
              key={level}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => onChange(level)}
              className={cn(
                "rounded-md border px-3 py-2 text-left text-sm transition",
                isActive
                  ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 text-foreground"
                  : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary))]/40 hover:text-foreground",
              )}
            >
              <div className="font-semibold capitalize">{level}</div>
              <div className="mt-0.5 text-xs leading-snug">
                {LABELS[level].title}
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        {LABELS[value].hint}
      </p>
    </div>
  );
}
